import { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { InputSwitch } from 'primereact/inputswitch';
import {
  campaignStatus,
  cityOptions,
  excerpt,
  matchesCity,
  packRows,
  sortFeed,
  stateOptions,
} from './newsFeedShared';

const CITIES = cityOptions();
const STATES = stateOptions();

function inlineText(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

export function RichBody({ text, className = '' }) {
  const blocks = String(text || '')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (!blocks.length) return null;
  return (
    <div className={`feed-rich ${className}`}>
      {blocks.map((block, index) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        const bullets = lines.length > 0 && lines.every((line) => line.startsWith('- '));
        if (bullets) {
          return (
            <ul key={index}>
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{inlineText(line.slice(2))}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{inlineText(block)}</p>;
      })}
    </div>
  );
}

function Cover({ item, className = '' }) {
  if (!item?.imageUrl) {
    return <div className={`feed-cover is-empty ${className}`} />;
  }
  return <img className={`feed-cover ${className}`} src={item.imageUrl} alt="" />;
}

function AdTag({ item }) {
  if (item?.type !== 'ad') return null;
  return <span className="feed-ad-tag">Ad</span>;
}

export function FeedListCard({ item, onOpen }) {
  return (
    <button type="button" className="feed-list-card" onClick={() => onOpen?.(item)}>
      <div className="feed-list-copy">
        <AdTag item={item} />
        {item.isPinned && item.type === 'news' ? <span className="feed-pin">Pinned</span> : null}
        <strong>{item.title}</strong>
        <span>{excerpt(item.body)}</span>
      </div>
      <Cover item={item} className="is-thumb" />
    </button>
  );
}

export function FeedGridCard({ item, onOpen, loneHalf = false }) {
  return (
    <button
      type="button"
      className={`feed-grid-card ${item.gridSpan === 'half' ? 'is-half' : 'is-full'} ${loneHalf ? 'is-lone' : ''}`}
      onClick={() => onOpen?.(item)}
    >
      <Cover item={item} className="is-grid" />
      <div className="feed-grid-copy">
        <AdTag item={item} />
        <strong>{item.title}</strong>
      </div>
    </button>
  );
}

function Cta({ item }) {
  if (item?.type !== 'ad' || !item?.ctaUrl) return null;
  return (
    <a className="feed-cta" href={item.ctaUrl} target="_blank" rel="noreferrer">
      {item.ctaLabel || 'Learn more'}
    </a>
  );
}

export function FeedTemplate({ item }) {
  const gallery = [
    item?.imageUrl,
    ...(item?.gallery || []).map((image) => image.url || image),
  ].filter(Boolean);
  const when = item?.startsAt
    ? new Date(item.startsAt).toLocaleDateString()
    : '';

  if (item?.templateId === 'news_tpl_2') {
    return (
      <article className="feed-detail tpl-editorial">
        <p className="feed-kicker">News</p>
        <h3>{item.title}</h3>
        <Cover item={item} className="is-editorial" />
        <RichBody text={item.body} />
      </article>
    );
  }

  if (item?.templateId === 'news_tpl_3') {
    return (
      <article className="feed-detail tpl-report">
        <h3>{item.title}</h3>
        <div className="feed-report-grid">
          <RichBody text={item.body} />
          <aside>
            <Cover item={item} className="is-side" />
            {when ? <span>{when}</span> : null}
          </aside>
        </div>
      </article>
    );
  }

  if (item?.templateId === 'ad_tpl_2') {
    return (
      <article className="feed-detail tpl-gallery">
        <AdTag item={item} />
        {item.advertiserName ? <p className="feed-kicker">{item.advertiserName}</p> : null}
        <h3>{item.title}</h3>
        <div className="feed-gallery-row">
          {gallery.map((url) => (
            <img key={url} src={url} alt="" />
          ))}
        </div>
        <RichBody text={item.body} />
        <Cta item={item} />
      </article>
    );
  }

  if (item?.templateId === 'ad_tpl_3') {
    return (
      <article className="feed-detail tpl-flyer">
        <Cover item={item} className="is-flyer" />
        <div className="feed-flyer-copy">
          <AdTag item={item} />
          {item.advertiserName ? <p className="feed-kicker">{item.advertiserName}</p> : null}
          <h3>{item.title}</h3>
          <Cta item={item} />
        </div>
      </article>
    );
  }

  if (item?.templateId === 'ad_tpl_1') {
    return (
      <article className="feed-detail tpl-banner">
        <Cover item={item} className="is-hero" />
        <AdTag item={item} />
        {item.advertiserName ? <p className="feed-kicker">{item.advertiserName}</p> : null}
        <h3>{item.title}</h3>
        <RichBody text={item.body} />
        <Cta item={item} />
      </article>
    );
  }

  return (
    <article className="feed-detail tpl-hero">
      <div className="feed-hero-wrap">
        <Cover item={item} className="is-hero" />
        <h3>{item.title}</h3>
      </div>
      <RichBody text={item.body} />
    </article>
  );
}

export function TemplateDialog({ item, visible, onHide }) {
  return (
    <Dialog
      header={item?.title || 'Preview'}
      visible={visible && Boolean(item)}
      style={{ width: 'min(440px, 96vw)' }}
      onHide={onHide}
    >
      {item ? (
        <div className="feed-phone is-dialog">
          <FeedTemplate item={item} />
        </div>
      ) : null}
    </Dialog>
  );
}

const PHONE_TABS = [
  { label: 'Noticias', icon: 'news', active: true },
  { label: 'Mapa', icon: 'map' },
  { label: 'Tiempos', icon: 'clock' },
  { label: 'Chat', icon: 'chat' },
  { label: 'Perfil', icon: 'person' },
];

function PhoneTabIcon({ name }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 18,
    height: 18,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  if (name === 'news') {
    return (
      <svg {...common}>
        <path d="M5 4.5h11.5A1.5 1.5 0 0 1 18 6v13.5H7.5A2.5 2.5 0 0 1 5 17V4.5z" />
        <path d="M8 8.5h7M8 12h7M8 15.5h4" />
      </svg>
    );
  }
  if (name === 'map') {
    return (
      <svg {...common}>
        <path d="M9 18.5 4.5 17V6.5L9 8l6-2 4.5 1.5V17L15 15.5 9 18.5z" />
        <path d="M9 8v10.5M15 6.5V15.5" />
      </svg>
    );
  }
  if (name === 'clock') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="7.5" />
        <path d="M12 8.5V12l2.5 2" />
      </svg>
    );
  }
  if (name === 'chat') {
    return (
      <svg {...common}>
        <path d="M6 16.5 4.5 19.5V7.5A2 2 0 0 1 6.5 5.5h11a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="9" r="3" />
      <path d="M6.5 18.5c.8-2.4 2.8-3.5 5.5-3.5s4.7 1.1 5.5 3.5" />
    </svg>
  );
}

function PhoneNav() {
  return (
    <div className="phone-nav" aria-hidden="true">
      {PHONE_TABS.map((tab) => (
        <div key={tab.label} className={tab.active ? 'is-active' : ''}>
          <PhoneTabIcon name={tab.icon} />
          <span>{tab.label}</span>
        </div>
      ))}
    </div>
  );
}

function matchesState(item, stateCode) {
  if (!stateCode) return true;
  const codes = item?.cityCodes || [];
  if (!codes.length) return true;
  const inState = new Set(
    CITIES.filter((city) => city.stateCode === stateCode).map((city) => city.value)
  );
  return codes.some((code) => inState.has(code));
}

export function NewsSimulator({
  items,
  cityCode,
  onCityChange,
  view,
  onViewChange,
  liveOnly,
  onLiveOnly,
  onOpen,
}) {
  const [stateCode, setStateCode] = useState(
    () => CITIES.find((city) => city.value === cityCode)?.stateCode || 'BC'
  );
  const cities = useMemo(
    () => (stateCode ? CITIES.filter((city) => city.stateCode === stateCode) : CITIES),
    [stateCode]
  );
  const now = new Date();
  const visible = sortFeed(items).filter((item) => {
    if (cityCode) {
      if (!matchesCity(item, cityCode)) return false;
    } else if (!matchesState(item, stateCode)) {
      return false;
    }
    if (liveOnly && campaignStatus(item, now) !== 'live') return false;
    return true;
  });
  const rows = packRows(visible);

  const onState = (next) => {
    setStateCode(next);
    const nextCities = next ? CITIES.filter((city) => city.stateCode === next) : CITIES;
    if (cityCode && !nextCities.some((city) => city.value === cityCode)) {
      onCityChange(nextCities[0]?.value || '');
    }
  };

  return (
    <Card className="news-sim-card">
      <aside className="news-sim">
        <div className="news-sim-head">
          <h3>Vista previa</h3>
          <p>Así se ve el feed en la app.</p>
        </div>
        <label className="news-sim-field">
          State
          <Dropdown
            value={stateCode}
            options={[{ label: 'All states', value: '' }, ...STATES]}
            onChange={(e) => onState(e.value || '')}
            filter
            className="w-full"
          />
        </label>
        <label className="news-sim-field">
          City
          <Dropdown
            value={cityCode}
            options={[
              { label: 'All cities', value: '' },
              ...cities.map((city) => ({
                value: city.value,
                label: stateCode ? city.cityName : `${city.cityName} · ${city.stateName}`,
              })),
            ]}
            onChange={(e) => onCityChange(e.value || '')}
            filter
            className="w-full"
            placeholder="City"
          />
        </label>
        <div className="news-sim-controls">
          <SelectButton
            value={view}
            options={[
              { label: 'List', value: 'list' },
              { label: 'Grid', value: 'grid' },
            ]}
            onChange={(e) => e.value && onViewChange(e.value)}
          />
          <label className="news-sim-live">
            <InputSwitch checked={liveOnly} onChange={(e) => onLiveOnly(Boolean(e.value))} />
            <span>Live only</span>
          </label>
        </div>
        <div className="phone-frame">
          <div className="phone-island" />
          <div className="feed-phone">
            <div className="phone-status">
              <span>9:41</span>
              <span>5G</span>
            </div>
            <div className="phone-scroll">
              {visible.length === 0 ? (
                <p className="feed-phone-empty">Nothing live for this city.</p>
              ) : view === 'list' ? (
                visible.map((item) => (
                  <FeedListCard key={item.id || item.title} item={item} onOpen={onOpen} />
                ))
              ) : (
                rows.map((row, index) => (
                  <div className="feed-grid-row" key={row.map((item) => item.id).join('-') || index}>
                    {row.map((item) => (
                      <FeedGridCard
                        key={item.id}
                        item={item}
                        onOpen={onOpen}
                        loneHalf={row.length === 1 && item.gridSpan === 'half'}
                      />
                    ))}
                  </div>
                ))
              )}
              <div className="feed-wa">Anúnciate aquí</div>
            </div>
            <PhoneNav />
          </div>
          <div className="phone-home" />
        </div>
      </aside>
    </Card>
  );
}
